import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Check, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { healthQuestionnaireService, HealthQuestionnaireDto } from '../services/health-questionnaire.service';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';

interface HealthQuestionnaireProps {
  onComplete: () => void;
  onSkip?: () => void;
}

export function HealthQuestionnaire({ onComplete, onSkip }: HealthQuestionnaireProps) {
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState(0);
  const [formData, setFormData] = useState<HealthQuestionnaireDto>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadExisting();
  }, [user]);

  const loadExisting = async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      const data = await healthQuestionnaireService.getMine();
      if (data) setFormData(data);
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof HealthQuestionnaireDto, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleArrayToggle = (field: 'currentMedications' | 'healthConditions' | 'symptoms', value: string) => {
    setFormData((prev) => {
      const arr = (prev[field] as string[]) || [];
      const next = arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
      return { ...prev, [field]: next };
    });
  };

  /** Client-side validation matching backend DTO rules */
  const validateForm = (): string | null => {
    const { age, heightCm, weightKg, waistCircumferenceCm, hba1cLevel, fastingReading, postMealReading } = formData;
    if (age != null && (age < 1 || age > 120)) return 'Age must be between 1 and 120 years';
    if (heightCm != null && (heightCm < 50 || heightCm > 250)) return 'Height must be between 50 and 250 cm';
    if (weightKg != null && (weightKg < 20 || weightKg > 300)) return 'Weight must be between 20 and 300 kg';
    if (waistCircumferenceCm != null && (waistCircumferenceCm < 40 || waistCircumferenceCm > 200)) return 'Waist circumference must be between 40 and 200 cm';
    if (hba1cLevel != null && (hba1cLevel < 4 || hba1cLevel > 20)) return 'HbA1c must be between 4 and 20%';
    if (fastingReading != null && (fastingReading < 0 || fastingReading > 600)) return 'Fasting reading must be between 0 and 600 mg/dL';
    if (postMealReading != null && (postMealReading < 0 || postMealReading > 600)) return 'Post-meal reading must be between 0 and 600 mg/dL';
    return null;
  };

  const handleSubmit = async () => {
    if (!user) return;
    const err = validateForm();
    if (err) {
      toast.error(err);
      return;
    }
    setIsSubmitting(true);
    try {
      await healthQuestionnaireService.submit(formData);
      toast.success('Questionnaire saved!');
      onComplete();
    } catch (err) {
      toast.error(handleApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const btn = (opt: string, field: keyof HealthQuestionnaireDto) =>
    `px-4 py-3 rounded-xl border-2 font-medium transition-all ${
      formData[field] === opt ? 'border-dropsugar-green bg-dropsugar-green/10 text-dropsugar-green' : 'border-gray-300 hover:border-gray-400'
    }`;

  const btnBool = (val: boolean, field: keyof HealthQuestionnaireDto) =>
    `px-4 py-3 rounded-xl border-2 font-medium transition-all ${
      formData[field] === val ? 'border-dropsugar-green bg-dropsugar-green/10 text-dropsugar-green' : 'border-gray-300 hover:border-gray-400'
    }`;

  const pages = [
    {
      title: 'Basic Information',
      content: (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Age (years)</label>
            <input
              type="number"
              min={1}
              max={120}
              value={formData.age ?? ''}
              onChange={(e) => handleChange('age', parseInt(e.target.value) || undefined)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-dropsugar-green focus:border-transparent"
              placeholder="1–120"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
            <div className="grid grid-cols-3 gap-3">
              {['Male', 'Female', 'Other'].map((opt) => (
                <button key={opt} type="button" onClick={() => handleChange('gender', opt)} className={btn(opt, 'gender')}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Height (cm)</label>
            <input
              type="number"
              min={50}
              max={250}
              value={formData.heightCm ?? ''}
              onChange={(e) => handleChange('heightCm', parseFloat(e.target.value) || undefined)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-dropsugar-green"
              placeholder="50–250"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Weight (kg)</label>
            <input
              type="number"
              min={20}
              max={300}
              value={formData.weightKg ?? ''}
              onChange={(e) => handleChange('weightKg', parseFloat(e.target.value) || undefined)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-dropsugar-green"
              placeholder="20–300"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Waist circumference (cm) - Optional</label>
            <input
              type="number"
              min={40}
              max={200}
              value={formData.waistCircumferenceCm ?? ''}
              onChange={(e) => handleChange('waistCircumferenceCm', parseFloat(e.target.value) || undefined)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-dropsugar-green"
              placeholder="40–200 (optional)"
            />
          </div>
        </div>
      ),
    },
    {
      title: 'Diabetes & Sugar Status',
      content: (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Have you ever been diagnosed with diabetes?</label>
            {['No', 'Yes – Type 2', 'Yes – Type 1', 'Gestational diabetes (past)'].map((opt) => (
              <button key={opt} type="button" onClick={() => handleChange('diabetesDiagnosis', opt)} className={`block w-full mb-2 text-left ${btn(opt, 'diabetesDiagnosis')}`}>
                {opt}
              </button>
            ))}
          </div>
          {formData.diabetesDiagnosis && formData.diabetesDiagnosis !== 'No' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Duration of diabetes</label>
                <div className="grid grid-cols-2 gap-2">
                  {['<1 year', '1–5 years', '5–10 years', '>10 years'].map((opt) => (
                    <button key={opt} type="button" onClick={() => handleChange('diabetesDuration', opt)} className={btn(opt, 'diabetesDuration')}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Last known HbA1c (%) - Optional</label>
                <input
                  type="number"
                  step="0.1"
                  min={4}
                  max={20}
                  value={formData.hba1cLevel ?? ''}
                  onChange={(e) => handleChange('hba1cLevel', parseFloat(e.target.value) || undefined)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-dropsugar-green"
                  placeholder="4–20"
                />
              </div>
            </>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Do you check blood sugar at home?</label>
            <div className="grid grid-cols-3 gap-2">
              {['No', 'Occasionally', 'Regularly'].map((opt) => (
                <button key={opt} type="button" onClick={() => handleChange('homeBloodSugarCheck', opt)} className={btn(opt, 'homeBloodSugarCheck')}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Fasting (mg/dL) - Optional</label>
              <input
                type="number"
                min={0}
                max={600}
                value={formData.fastingReading ?? ''}
                onChange={(e) => handleChange('fastingReading', parseFloat(e.target.value) || undefined)}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-dropsugar-green"
                placeholder="0–600"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Post-meal (mg/dL) - Optional</label>
              <input
                type="number"
                min={0}
                max={600}
                value={formData.postMealReading ?? ''}
                onChange={(e) => handleChange('postMealReading', parseFloat(e.target.value) || undefined)}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-dropsugar-green"
                placeholder="0–600"
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Current Medications',
      content: (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Are you on any medications?</label>
            <div className="space-y-2">
              {['No', 'Diabetes tablets', 'Insulin', 'Both'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => handleArrayToggle('currentMedications', opt)}
                  className={`block w-full text-left flex items-center justify-between ${formData.currentMedications?.includes(opt) ? 'border-dropsugar-green bg-dropsugar-green/10 text-dropsugar-green' : 'border-gray-300'} px-4 py-3 rounded-xl border-2 font-medium`}
                >
                  {opt}
                  {formData.currentMedications?.includes(opt) && <Check className="w-5 h-5" />}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Names of medicines (if known)</label>
            <textarea
              value={formData.medicationNames ?? ''}
              onChange={(e) => handleChange('medicationNames', e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-dropsugar-green"
              rows={4}
              placeholder="Enter medication names (optional)"
            />
          </div>
        </div>
      ),
    },
    {
      title: 'Weight & Metabolic Health',
      content: (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Have you gained weight in the last 2–3 years?</label>
            <div className="grid grid-cols-2 gap-3">
              {[{ label: 'No', value: false }, { label: 'Yes', value: true }].map((opt) => (
                <button key={opt.label} type="button" onClick={() => handleChange('recentWeightGain', opt.value)} className={btnBool(opt.value, 'recentWeightGain')}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Do you have any of the following? (Select all that apply)</label>
            <div className="space-y-2">
              {['High BP', 'High cholesterol', 'Fatty liver', 'PCOS', 'Thyroid disorder', 'Heart disease', 'Kidney disease'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => handleArrayToggle('healthConditions', opt)}
                  className={`block w-full text-left flex items-center justify-between ${formData.healthConditions?.includes(opt) ? 'border-dropsugar-green bg-dropsugar-green/10 text-dropsugar-green' : 'border-gray-300'} px-4 py-3 rounded-xl border-2 font-medium`}
                >
                  {opt}
                  {formData.healthConditions?.includes(opt) && <Check className="w-5 h-5" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Lifestyle',
      content: (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Physical activity level</label>
            <div className="space-y-2">
              {['Sedentary', 'Mild (walking <30 min/day)', 'Moderate (exercise ≥30 min/day)', 'Heavy (gym/manual work)'].map((opt) => (
                <button key={opt} type="button" onClick={() => handleChange('physicalActivityLevel', opt)} className={`block w-full text-left ${btn(opt, 'physicalActivityLevel')}`}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Average sleep duration</label>
            <div className="grid grid-cols-3 gap-2">
              {['<6 hours', '6–7 hours', '>7 hours'].map((opt) => (
                <button key={opt} type="button" onClick={() => handleChange('averageSleepDuration', opt)} className={btn(opt, 'averageSleepDuration')}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Diet Pattern',
      content: (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Your usual diet</label>
            <div className="grid grid-cols-3 gap-2">
              {['Vegetarian', 'Non-vegetarian', 'Mixed'].map((opt) => (
                <button key={opt} type="button" onClick={() => handleChange('dietType', opt)} className={btn(opt, 'dietType')}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">How often do you eat sweets, bakery items, or processed food?</label>
            <div className="grid grid-cols-3 gap-2">
              {['Rarely', 'Weekly', 'Daily'].map((opt) => (
                <button key={opt} type="button" onClick={() => handleChange('processedFoodFrequency', opt)} className={btn(opt, 'processedFoodFrequency')}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Habits',
      content: (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tobacco use</label>
            <div className="grid grid-cols-2 gap-3">
              {[{ label: 'No', value: false }, { label: 'Yes', value: true }].map((opt) => (
                <button key={opt.label} type="button" onClick={() => handleChange('tobaccoUse', opt.value)} className={btnBool(opt.value, 'tobaccoUse')}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Alcohol intake</label>
            <div className="grid grid-cols-3 gap-2">
              {['None', 'Occasional', 'Regular'].map((opt) => (
                <button key={opt} type="button" onClick={() => handleChange('alcoholIntake', opt)} className={btn(opt, 'alcoholIntake')}>
                  {opt}
                </button>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Symptoms (Last 3 Months)',
      content: (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Do you experience any of the following? (Select all that apply)</label>
            <div className="space-y-2">
              {['Excess hunger', 'Excess thirst', 'Frequent urination', 'Fatigue', 'Blurred vision', 'Tingling or numbness in feet', 'Erectile dysfunction (men)'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => handleArrayToggle('symptoms', opt)}
                  className={`block w-full text-left flex items-center justify-between ${formData.symptoms?.includes(opt) ? 'border-dropsugar-green bg-dropsugar-green/10 text-dropsugar-green' : 'border-gray-300'} px-4 py-3 rounded-xl border-2 font-medium`}
                >
                  {opt}
                  {formData.symptoms?.includes(opt) && <Check className="w-5 h-5" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Insulin Resistance Red-Flag Check',
      content: (
        <div className="space-y-6">
          <p className="text-sm text-gray-600 mb-4">Answer YES or NO to the following questions:</p>
          {[
            {
              q: 'Do you gain weight easily around your abdomen, even with modest food intake?',
              hint: 'Central (belly) fat is insulin resistance waving a red flag.',
              field: 'abdominalWeightGain' as const,
            },
            {
              q: 'Do you feel sleepy, tired, or mentally foggy after meals—especially after rice, bread, or sweets?',
              hint: 'Post-meal sleepiness is a classic sign of excess insulin.',
              field: 'postMealFatigue' as const,
            },
            {
              q: 'Have you been told your triglycerides are high or your HDL ("good cholesterol") is low?',
              hint: 'This lipid pattern is insulin resistance in disguise.',
              field: 'lipidIssues' as const,
            },
            {
              q: 'Do you experience intense hunger or sugar cravings within 2–3 hours of eating?',
              hint: 'This rapid glucose–insulin roller coaster is not normal physiology.',
              field: 'rapidHunger' as const,
            },
            {
              q: 'Do you have darkened skin over the neck, armpits, or groin, or a strong family history of diabetes?',
              hint: null,
              field: 'skinChangesFamilyHistory' as const,
            },
            {
              q: 'Do you feel bloated most of the time, or has anyone told you that you have fatty liver?',
              hint: null,
              field: 'bloatingFattyLiver' as const,
            },
            {
              q: '(For women) Do you have irregular periods, unwanted facial/body hair, acne, or mood swings?',
              hint: 'These are hormonal effects driven by insulin resistance and are common in PCOS.',
              field: 'hormonalIssues' as const,
            },
          ].map(({ q, hint, field }) => (
            <div key={field}>
              <label className="block text-sm font-medium text-gray-900 mb-1">{q}</label>
              {hint && <p className="text-xs text-gray-600 mb-2">{hint}</p>}
              <div className="grid grid-cols-2 gap-3">
                {[{ label: 'No', value: false }, { label: 'Yes', value: true }].map((opt) => (
                  <button key={opt.label} type="button" onClick={() => handleChange(field, opt.value)} className={btnBool(opt.value, field)}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-dropsugar-green border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const progressPercentage = ((currentPage + 1) / pages.length) * 100;
  const page = pages[currentPage];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white p-6">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-3xl shadow-xl p-8">
          {/* Stepper header - like nz-dropsugar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Health Assessment</h2>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">
                  {currentPage + 1} / {pages.length}
                </span>
                {onSkip && (
                  <button onClick={onSkip} className="text-gray-500 hover:text-gray-700 transition-colors p-1" title="Skip to packages">
                    <X className="w-6 h-6" />
                  </button>
                )}
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-dropsugar-green to-dropsugar-green-dark h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">{page.title}</h3>
            {page.content}
          </div>

          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentPage((p) => p - 1)}
                disabled={currentPage === 0}
                className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all ${
                  currentPage === 0 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <ChevronLeft className="w-5 h-5 mr-1" />
                Previous
              </button>
              {onSkip && (
                <button onClick={onSkip} className="px-6 py-3 text-gray-600 hover:bg-gray-100 rounded-xl font-medium transition-all">
                  Skip for now
                </button>
              )}
            </div>
            {currentPage < pages.length - 1 ? (
              <button
                onClick={() => setCurrentPage((p) => p + 1)}
                className="flex items-center px-6 py-3 bg-dropsugar-green hover:bg-dropsugar-green-dark text-white rounded-xl font-medium transition-all"
              >
                Next
                <ChevronRight className="w-5 h-5 ml-1" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center px-8 py-3 bg-gradient-to-r from-dropsugar-green to-dropsugar-green-dark hover:opacity-90 text-white rounded-xl font-medium transition-all disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Complete Assessment'}
                <Check className="w-5 h-5 ml-2" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
